import React, {createContext, useContext, useEffect, useState, useRef} from 'react';
import {useFetchHook} from "../hooks/useFetchHook";
import fetcher from "../fetcher";
import {useParams} from "react-router";
import useDidMountEffect from "../hooks/useDidMountEffect";
import {useDebounce, usePrevious} from "@uidotdev/usehooks";
import useEnterEffect from "../hooks/useEnterEffect";
import {useUserContext} from "./UserContext";
import {toast} from "react-toastify";
import {DeskingModal} from "../pages/PricingListPage/DeskingModal";
import {useModalsStore} from "../stores/modalsStore";


export const PURCHASE_DEAL_LIGHT = '#1BA897'
export const PURCHASE_DEAL_DARK = '#007D6E'
export const LEASE_DEAL_DARK = '#2267CE'
// export const LEASE_DEAL_LIGHT = '#2B8EEA'
export const LEASE_DEAL_LIGHT = '#2267CE'

export const DealContext = createContext(undefined)

const getInitialState = (location) => {
    return {
        msrp: '',
        cost: '',
        description: '',
        invoice: 0,
        above: 0,
        back: 0,
        profit: 0,
        car: null,
        is_purchase: false,
        location: location,
        location_id: location.id,
        drive_off: null,
        term: 36,
        mileage: 10_000,

        address_line_one: location.address_line_one,
        address_line_two: location.address_line_two,
        city: location.city,
        state: location.state,
        zipcode: location.zipcode,
        county: location.county,
    }
}

export const DealContextProvider = ( { children, id, isDeskingContext, ...props } ) => {
    const { profileState } = useUserContext()
    const openModal = useModalsStore((state) => state.openModal)
    const params = useParams();

    const dealId = () => {
        return id || params.id
    }

    const [dealState, setDealState] = useState(null)

    const dealPageRef = useRef(null)

    const [createDealResponse, createDeal] = useFetchHook(fetcher.createDeal)
    const [cloneDealResponse, cloneDeal] = useFetchHook(fetcher.createDeal)
    const [fetchDealResponse, fetchDeal] = useFetchHook(fetcher.getDeal)
    const [updateDealResponse, updateDeal] = useFetchHook(fetcher.updateDeal)
    const [getDeskingResponse, fetchDesking] = useFetchHook(fetcher.getDesking)

    const [updateDealWithoutQuoteUpdateResponse, updateDealWithoutQuoteUpdate] = useFetchHook(fetcher.updateDeal)

    const debouncedDeal = useDebounce(dealState, 400)

    const prevState = usePrevious(dealState);

    const dealColorDark = () => {
        return dealState.is_purchase ? PURCHASE_DEAL_DARK : LEASE_DEAL_DARK
    }

    const dealColorLight = () => {
        return dealState.is_purchase ? PURCHASE_DEAL_LIGHT : LEASE_DEAL_DARK
    }

    const dealColorDarkAlt = () => {
        return dealState.is_purchase ? LEASE_DEAL_DARK : PURCHASE_DEAL_DARK
    }

    const dealColorLightAlt = () => {
        return dealState.is_purchase ? LEASE_DEAL_DARK : PURCHASE_DEAL_LIGHT
    }

    const setDealStateWithUpdate = (newState, shouldUpdate = false) => {
        setDealState({...dealState, ...newState, _updateInBackend: shouldUpdate })
    }

    const setDealStateWithoutUpdate = (newState) => {
        setDealState(newState)
    }

    const setDealStateWithoutQuoteUpdate = (newState) => {
        return updateDealWithoutQuoteUpdate(dealState.id, {...newState, _no_update: true})
    }

    const updateDealFunc = () => {
        if (dealState.formatted_quote.source === 'PARTNER_API') {
            openModal({
                id: "DeskingModal",
                element: DeskingModal,
                props: {
                    id: dealState.id,
                    deal_id: dealState.id,
                    ...dealState,
                    onSave: (updateData) => setDealStateWithUpdate(updateData, false),
                }
            })
            return
        }
        const updateObj = structuredClone(dealState)
        delete updateObj._updateInBackend
        return updateDeal(dealState.id, updateObj)
    }

    const createUpdateFunc = () => {
        const updateObj = structuredClone(dealState)

        delete updateObj._updateInBackend
        if (updateObj.id) {
            return updateDealFunc()
        } else {
            return createDeal(updateObj).then((data) => {
                openModal({
                    id: "DeskingModal",
                    element: DeskingModal,
                    props: {
                        id: data.data.id,
                        deal_id: data.data.id
                    }
                })
            })
        }
    }

    const resetDealState = () => {
        setDealState(getInitialState(profileState.default_location))
    }

    useEnterEffect(function updateDealOnEnter(){
        if (dealState.base_car && dealState.msrp && dealState.cost && !updateDealResponse.loading) {
            if (!dealState.is_locked) {
                createUpdateFunc()
            } else {
                setDealStateWithoutUpdate(prevState)
                toast.warn("This deal is locked and cannot be updated.")
            }

        }
    }, [dealState, updateDealResponse])

    useEffect(function fetchDealUsingID(){
        if (dealId()) {
            if (isDeskingContext) {
                fetchDesking(dealId())
            } else {
                fetchDeal(dealId())
            }
        } else {
            setDealState(getInitialState(profileState.default_location))
        }
    }, [dealId()])

    useEffect(function setFetchedQuote(){
        if (fetchDealResponse?.data?.data) {
            setDealStateWithoutUpdate(fetchDealResponse?.data?.data)
        }
    }, [fetchDealResponse])

    useEffect(function setCreatedQuote(){
        if (createDealResponse?.data?.data) {
            setDealStateWithoutUpdate(createDealResponse?.data?.data)
        }
    }, [createDealResponse])

    useDidMountEffect(function updateQuoteInBackend(){
        if (dealState?._updateInBackend === true) {
            updateDealFunc()
        }
    }, [debouncedDeal])

    useEffect(function returnToPreviousState() {
        if (updateDealResponse?.error && !updateDealResponse.loading) {
            setDealStateWithoutUpdate(prevState)
        }
    }, [updateDealResponse])

    useDidMountEffect(function updateQuoteFromBackend(){
        if (updateDealResponse?.data?.data && !updateDealResponse.loading && !updateDealResponse.error) {
            const newDealState = updateDealResponse?.data?.data
            setDealStateWithoutUpdate(newDealState)

            const expirationDate = newDealState.formatted_quote.expiration_date
            const isExpired = new Date(expirationDate).setUTCHours(0,0,0,0) <= new Date().setUTCHours(0,0,0,0)

            if (isExpired) {
                toast.warn("This program is expired.")
            } else {
                toast.success("Successfully updated quote.")
            }

            props.onSave && props.onSave(newDealState)
        }
    }, [updateDealResponse])

    useDidMountEffect(function updateDealFromBackendWhenNotUpdatingQuote(){
        if (updateDealWithoutQuoteUpdateResponse?.data?.data && !updateDealWithoutQuoteUpdateResponse.loading && !updateDealWithoutQuoteUpdateResponse.error) {
            const newDealState = updateDealWithoutQuoteUpdateResponse?.data?.data
            setDealStateWithoutUpdate(newDealState)

            const expirationDate = newDealState.formatted_quote.expiration_date
            const isExpired = new Date(expirationDate).setUTCHours(0,0,0,0) <= new Date().setUTCHours(0,0,0,0)

            if (isExpired) {
                toast.warn("This program is expired.")
            } else {
                toast.success("Successfully updated quote.")
            }

            props.onSave && props.onSave(newDealState)
        }
    }, [updateDealWithoutQuoteUpdateResponse])

    const value = {
        dealPageRef,
        dealState,

        createDeal,
        createDealResponse,

        updateDeal,
        updateDealResponse,

        fetchDeal,
        fetchDealResponse,

        cloneDeal,
        cloneDealResponse,

        dealId,
        setDealStateWithUpdate,
        setDealStateWithoutUpdate,
        setDealStateWithoutQuoteUpdate,

        dealColorDark,
        dealColorLight,
        dealColorDarkAlt,
        dealColorLightAlt,

        createUpdateFunc,
        resetDealState,
        ...props
    }

    if (!isDeskingContext) {
        if (!dealState) return null;

        if (dealId() && !dealState.id) return null;
    }

    return (
        <DealContext.Provider value={value}>
            { children }
        </DealContext.Provider>
    )
}

export class NoDealContextError extends Error {
    constructor(message) {
        super(message);
        this.name = 'NoDealContextError';
    }
}

export const useDealContext = () => {
    const context = useContext(DealContext)

    if (context === undefined) {
        throw new NoDealContextError('useDealContext must be used within DealContext')
    }
    return context
}